import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import CustomTrainingFormsService from '../../services/custom-training-forms.service';
import CustomTrainingFieldsService from '../../services/custom-training-fields.service';
import CustomTrainingFormDialog from '../dialogs/custom-training-form.dialog';
import CustomTrainingFieldDialog from '../dialogs/custom-training-field.dialog';
import {
  CreateFieldRequest,
  CreateFormRequest,
  CustomTrainingField,
  CustomTrainingForm,
  Team,
} from '../../models/training.models';
import { palette } from '../../theme';

import './tp-week-calendar.component.css';

interface TpFieldsFormsPanelProps {
  team: Team;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  number: 'Number',
  enum: 'Dropdown',
  rating: 'Rating',
  full_team_number: 'Per-Athlete',
  target_recording: 'Biathlon Target',
};

const FIELD_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  text: { bg: palette.grey[100], color: palette.grey[700] },
  number: { bg: '#E0E7FF', color: '#4338CA' },
  enum: { bg: palette.warning.light, color: palette.warning.dark },
  rating: { bg: palette.primary.light, color: palette.primary.main },
  full_team_number: { bg: palette.success.light, color: palette.success.dark },
  target_recording: { bg: palette.error.light, color: palette.error.main },
};

const TpFieldsFormsPanel: FC<TpFieldsFormsPanelProps> = (props) => {
  const formsService = useService(CustomTrainingFormsService);
  const fieldsService = useService(CustomTrainingFieldsService);
  const { showSnackbar } = useSnackbarHelper();

  const [forms, setForms] = useState<CustomTrainingForm[]>([]);
  const [fields, setFields] = useState<CustomTrainingField[]>([]);
  const [loading, setLoading] = useState(true);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formDialogLoading, setFormDialogLoading] = useState(false);
  const [editingForm, setEditingForm] = useState<CustomTrainingForm | null>(null);
  const [formToDelete, setFormToDelete] = useState<CustomTrainingForm | null>(null);

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [fieldDialogLoading, setFieldDialogLoading] = useState(false);
  const [editingField, setEditingField] = useState<CustomTrainingField | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<CustomTrainingField | null>(null);

  const [formMenuAnchor, setFormMenuAnchor] = useState<null | HTMLElement>(null);
  const [formMenuTarget, setFormMenuTarget] = useState<CustomTrainingForm | null>(null);
  const [fieldMenuAnchor, setFieldMenuAnchor] = useState<null | HTMLElement>(null);
  const [fieldMenuTarget, setFieldMenuTarget] = useState<CustomTrainingField | null>(null);

  const [previewForm, setPreviewForm] = useState<CustomTrainingForm | null>(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([formsService.listForms(props.team.id as string), fieldsService.listFields(props.team.id as string)])
      .then(([formsRes, fieldsRes]) => {
        setForms(formsRes.data.data || []);
        setFields(fieldsRes.data.data || []);
      })
      .catch(() => showSnackbar('Error loading fields and forms', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fieldUsageMap = useMemo(() => {
    const map: Record<number, string[]> = {};
    forms.forEach((form) => {
      form.fields.forEach((field) => {
        if (!map[field.field_id]) map[field.field_id] = [];
        map[field.field_id].push(form.name);
      });
    });
    return map;
  }, [forms]);

  const handleFormSubmit = (data: CreateFormRequest) => {
    setFormDialogLoading(true);
    const request = editingForm
      ? formsService.updateForm(props.team.id as string, editingForm.id, data)
      : formsService.createForm(props.team.id as string, data);

    request
      .then(() => {
        showSnackbar(editingForm ? 'Form updated' : 'Form created', 'success');
        setFormDialogOpen(false);
        setEditingForm(null);
        setFormDialogLoading(false);
        fetchAll();
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || 'Error saving form', 'error');
        setFormDialogLoading(false);
      });
  };

  const handleFieldSubmit = (data: CreateFieldRequest) => {
    setFieldDialogLoading(true);
    const request = editingField
      ? fieldsService.updateField(props.team.id as string, editingField.id, data)
      : fieldsService.createField(props.team.id as string, data);

    request
      .then(() => {
        showSnackbar(editingField ? 'Field updated' : 'Field created', 'success');
        setFieldDialogOpen(false);
        setEditingField(null);
        setFieldDialogLoading(false);
        fetchAll();
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || 'Error saving field', 'error');
        setFieldDialogLoading(false);
      });
  };

  const handleDeleteForm = () => {
    if (!formToDelete) return;
    formsService
      .deleteForm(props.team.id as string, formToDelete.id)
      .then(() => {
        showSnackbar('Form archived', 'success');
        setFormToDelete(null);
        fetchAll();
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || 'Error archiving form', 'error');
        setFormToDelete(null);
      });
  };

  const handleDeleteField = () => {
    if (!fieldToDelete) return;
    fieldsService
      .deleteField(props.team.id as string, fieldToDelete.id)
      .then(() => {
        showSnackbar('Field archived', 'success');
        setFieldToDelete(null);
        fetchAll();
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || 'Error archiving field', 'error');
        setFieldToDelete(null);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <div className="tp-fields-forms">
      <div className="tp-ff-card">
        <div className="tp-ff-card__header">
          <div>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Custom Forms
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Post-training forms athletes fill out after sessions
            </Typography>
          </div>
          <Button
            variant="contained"
            onClick={() => {
              setEditingForm(null);
              setFormDialogOpen(true);
            }}
          >
            Create New Form
          </Button>
        </div>

        {forms.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No custom forms yet.
          </Typography>
        ) : (
          <div className="tp-form-grid">
            {forms.map((form) => (
              <div key={form.id} className="tp-form-item">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {form.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={form.active ? 'Active' : 'Draft'}
                    style={{
                      backgroundColor: form.active ? palette.primary.light : palette.warning.light,
                      color: form.active ? palette.primary.main : palette.warning.dark,
                      fontWeight: 600,
                    }}
                  />
                </div>
                {form.description && (
                  <Typography variant="body2" color="textSecondary">
                    {form.description}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  {form.fields.length} field{form.fields.length === 1 ? '' : 's'}
                </Typography>
                <div className="tp-form-item__actions">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setEditingForm(form);
                      setFormDialogOpen(true);
                    }}
                  >
                    Edit Form
                  </Button>
                  <Button size="small" onClick={() => setPreviewForm(form)}>
                    Preview
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="Form actions"
                    onClick={(e) => {
                      setFormMenuAnchor(e.currentTarget);
                      setFormMenuTarget(form);
                    }}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="tp-ff-card">
        <div className="tp-ff-card__header">
          <div>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Custom Fields
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Reusable fields for post-training forms
            </Typography>
          </div>
          <Button
            variant="outlined"
            onClick={() => {
              setEditingField(null);
              setFieldDialogOpen(true);
            }}
          >
            + New Field
          </Button>
        </div>

        {fields.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No custom fields yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Used In</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field) => {
                  const typeStyle = FIELD_TYPE_COLORS[field.field_type] || FIELD_TYPE_COLORS.text;
                  const usedIn = fieldUsageMap[field.id] || [];
                  return (
                    <TableRow key={field.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {field.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <span
                          className="tp-type-chip"
                          style={{ backgroundColor: typeStyle.bg, color: typeStyle.color }}
                        >
                          {FIELD_TYPE_LABELS[field.field_type] || field.field_type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {usedIn.length > 0 ? usedIn.join(', ') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="Field actions"
                          onClick={(e) => {
                            setFieldMenuAnchor(e.currentTarget);
                            setFieldMenuTarget(field);
                          }}
                        >
                          <MoreHorizIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <Menu
        anchorEl={formMenuAnchor}
        open={Boolean(formMenuAnchor)}
        onClose={() => {
          setFormMenuAnchor(null);
          setFormMenuTarget(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (formMenuTarget) {
              setEditingForm(formMenuTarget);
              setFormDialogOpen(true);
            }
            setFormMenuAnchor(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFormToDelete(formMenuTarget);
            setFormMenuAnchor(null);
          }}
          style={{ color: palette.error.main }}
        >
          Archive
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={fieldMenuAnchor}
        open={Boolean(fieldMenuAnchor)}
        onClose={() => {
          setFieldMenuAnchor(null);
          setFieldMenuTarget(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (fieldMenuTarget) {
              setEditingField(fieldMenuTarget);
              setFieldDialogOpen(true);
            }
            setFieldMenuAnchor(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFieldToDelete(fieldMenuTarget);
            setFieldMenuAnchor(null);
          }}
          style={{ color: palette.error.main }}
        >
          Archive
        </MenuItem>
      </Menu>

      <CustomTrainingFormDialog
        isOpen={formDialogOpen}
        handleClose={() => {
          setFormDialogOpen(false);
          setEditingForm(null);
          setFormDialogLoading(false);
        }}
        handleSubmit={handleFormSubmit}
        isLoading={formDialogLoading}
        existingForm={editingForm}
        teamId={props.team.id as string}
      />

      <CustomTrainingFieldDialog
        isOpen={fieldDialogOpen}
        handleClose={() => {
          setFieldDialogOpen(false);
          setEditingField(null);
          setFieldDialogLoading(false);
        }}
        handleSubmit={handleFieldSubmit}
        isLoading={fieldDialogLoading}
        existingField={editingField}
      />

      <Dialog open={Boolean(formToDelete)} onClose={() => setFormToDelete(null)}>
        <DialogTitle>Archive Custom Form</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive {formToDelete?.name}? This form will no longer be available for use.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteForm} color="error" variant="contained">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(fieldToDelete)} onClose={() => setFieldToDelete(null)}>
        <DialogTitle>Archive Custom Field</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive {fieldToDelete?.name}? This field will no longer be available for use.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteField} color="error" variant="contained">
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewForm)} onClose={() => setPreviewForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{previewForm?.name}</DialogTitle>
        <DialogContent dividers>
          {previewForm?.description && (
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              {previewForm.description}
            </Typography>
          )}
          <Chip size="small" label={previewForm?.active ? 'Active' : 'Draft'} style={{ marginBottom: 16 }} />
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            {previewForm?.fields.length || 0} fields
          </Typography>
          {(previewForm?.fields || []).map((field) => (
            <Box
              key={`${field.field_id}-${field.order}`}
              sx={{
                border: `1px solid ${palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1,
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {field.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {FIELD_TYPE_LABELS[field.field_type] || field.field_type}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewForm(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TpFieldsFormsPanel;
